import { getLatexMathParser } from "@/components/tiptap/latex-input/utils";
import { copyNodePropsFromDoc } from "@/models/nodes/copyNodePropsFromDoc";
import { normalizeMathText } from "@/utils/normalizeMathText";

import type { SingleSuggestion, SuggestionProvider } from "../types";
import type { NodeJson } from "@corca/data";

export const latexInputSuggestionProvider: SuggestionProvider = {
  type: "latexInput",
  search: (str, doc) => {
    if (!doc.editor.editorState.latexInput) {
      return [];
    }

    const parseMathLatex = getLatexMathParser();

    if (!parseMathLatex) {
      if (/^[$]\d+(\.\d*)?$/.test(str)) {
        return [[createCurrencySuggestion(str.substring(1))]];
      }

      return [];
    }

    const { latexInput } = doc.editor.editorState;

    if (!latexInput) return [];

    const latexContent = latexInput.content;

    if (latexContent.trim().length > 3 && !/[\d+-=/\\]/.test(latexContent)) {
      return [];
    }

    try {
      const normalizedLatexContent = normalizeMathText(latexContent);
      const nodeJson: NodeJson = parseMathLatex(normalizedLatexContent);

      if (nodeJson.type !== "inline" || nodeJson.children.length === 0) return [];

      const nodesJson = nodeJson.children.map((child) => copyNodePropsFromDoc(doc, child));

      const suggestions: SingleSuggestion[] = [];

      // Special case: "$123" should suggest currency format
      if (latexInput.start.type === "$" && !latexInput.end && /^\d+(\.\d*)?$/.test(latexContent)) {
        suggestions.push(createCurrencySuggestion(latexContent));
      }

      suggestions.push({
        type: "single",
        item: {
          type: "expression",
          symbol: "",
          displayName: "LaTeX",
          nodesJson,
          category: "",
        },
        canAutocomplete: false,
        canBeAutocompleted: false,
        action: {
          type: "insert-expression",
          nodesJson,
        },
        priority: 1,
      });

      return [suggestions];
    } catch {
      return [];
    }
  },
};

function createCurrencySuggestion(value: string): SingleSuggestion {
  const currNodesJson: NodeJson[] = [
    {
      type: "number",
      props: {
        value,
      },
      children: [
        {
          type: "unit",
          props: {
            placement: "prefix",
            unitType: "currency",
          },
          children: [
            {
              type: "inline",
              props: {},
              children: [
                {
                  type: "plainText",
                  props: {
                    value: "$",
                  },
                  children: [],
                },
              ],
            },
          ],
        },
      ],
    },
  ];
  return {
    type: "single",
    item: {
      type: "expression",
      symbol: "",
      displayName: "Currency",
      nodesJson: currNodesJson,
      category: "",
    },
    canAutocomplete: false,
    canBeAutocompleted: false,
    action: {
      type: "insert-expression",
      nodesJson: currNodesJson,
    },
    priority: 1,
  };
}
